/**
 * Auth API Service
 *
 * Специализированный сервис для работы с профилем пользователя.
 * Использует базовый apiClient для HTTP запросов с Keycloak аутентификацией.
 */

import { apiClient } from '@/api/client';
import type { DashboardResponseDto, UpdateProfileRequest, UserClubDto, UserProfile } from '@/types/models';

/**
 * Auth API Service
 *
 * Предоставляет методы для работы с профилем текущего пользователя.
 */
export const authApi = {
	/**
	 * Получить профиль текущего аутентифицированного пользователя
	 *
	 * @returns Promise с профилем пользователя
	 * @throws {ApiRequestError} При ошибке (401, network errors)
	 */
	getMe(): Promise<{ needsConsent: true } | UserProfile> {
		return apiClient.get<{ needsConsent: true } | UserProfile>('/auth/me', { useKeycloakAuth: true });
	},

	/**
	 * Обновить профиль текущего аутентифицированного пользователя
	 *
	 * @param data - Данные для частичного обновления профиля
	 * @returns Promise с обновлённым профилем пользователя
	 * @throws {ApiRequestError} При ошибке (401, 422, network errors)
	 */
	updateMe(data: UpdateProfileRequest): Promise<UserProfile> {
		return apiClient.patch<UserProfile, UpdateProfileRequest>('/auth/me', data, { useKeycloakAuth: true });
	},

	/**
	 * Получить список клубов текущего пользователя
	 *
	 * @returns Promise со списком клубов пользователя
	 * @throws {ApiRequestError} При ошибке (401, network errors)
	 */
	getMyClubs(): Promise<UserClubDto[]> {
		return apiClient.get<UserClubDto[]>('/auth/me/clubs', { useKeycloakAuth: true });
	},

	/**
	 * Retrieve role-based recent tournaments for the current user (dashboard data)
	 *
	 * @returns Promise with dashboard response containing up to 10 recent tournaments
	 * @throws {ApiRequestError} On 401 or network errors
	 */
	getDashboard(): Promise<DashboardResponseDto> {
		return apiClient.get<DashboardResponseDto>('/auth/me/dashboard', { useKeycloakAuth: true });
	},

	/**
	 * Record that the current user has accepted the Privacy Policy and Terms of Service
	 *
	 * @returns Promise with the timestamp when consent was accepted
	 * @throws {ApiRequestError} On 401 or network errors
	 */
	postConsent(): Promise<{ consentAcceptedAt: string }> {
		return apiClient.post<{ consentAcceptedAt: string }>('/auth/consent', {}, { useKeycloakAuth: true });
	},

	/**
	 * Delete the current user's account and anonymize all associated data
	 *
	 * @returns Promise that resolves when account deletion is complete
	 * @throws {ApiRequestError} On 401 or network errors
	 */
	deleteMe(): Promise<void> {
		return apiClient.delete<void>('/auth/me', { useKeycloakAuth: true });
	}
};
